import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Grid,
  Chip,
  Alert,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PaymentIcon from "@mui/icons-material/Payment";
import CircleIcon from "@mui/icons-material/Circle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MapIcon from "@mui/icons-material/Map";
import ScheduleIcon from "@mui/icons-material/Schedule";

import { getBookingDetails, cancelBookingById, refundBooking, cancelPassengerTicket } from "../api/bookings";
import { getTripDetails, downloadTickets, downloadPassengerTicket } from "../api";
import { initiatePayment, checkPaymentPrice } from "../utils/paymentFlow";
import { adjustTimes } from "../utils/DateTime";
import { getCityDisplay, getCityKey } from "../utils/city";
import { storeBookingAccess } from "../utils/bookingAccess";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../components/layout";

function refundTextForHours(hoursLeft, t) {
  if (hoursLeft >= 24) return t("refund.refundFullMinusFee");
  if (hoursLeft >= 2) return t("refund.refund90");
  return t("refund.refundNone");
}

const BookingDetails = () => {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [booking, setBooking] = useState(null);
  const [trip, setTrip] = useState(null);
  const [legTrips, setLegTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const [holdExpired, setHoldExpired] = useState(false);
  const [showAllStops, setShowAllStops] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [isRefunding, setIsRefunding] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [refundConfirmationOpen, setRefundConfirmationOpen] = useState(false);
  const [directionDialog, setDirectionDialog] = useState({
    open: false,
    leg: null,
    label: "",
    action: null,
  });
  const [confirmPassengerDialog, setConfirmPassengerDialog] = useState({
    open: false,
    passenger: null,
    direction: "",
  });
  const [priceChangeDialog, setPriceChangeDialog] = useState({
    open: false,
    bookedPrice: null,
    currentPrice: null,
  });

  useEffect(() => {
    const accessFromUrl = searchParams.get("access");
    if (id && accessFromUrl) {
      storeBookingAccess(id, accessFromUrl);
    }
    const fetchBooking = async () => {
      try {
        const data = await getBookingDetails(id);
        setBooking(data);
        if ((data?.is_transfer || data?.is_round_trip) && data.legs?.length) {
          const tripsData = await Promise.all(
            data.legs.map((leg) => getTripDetails(leg.trip_id))
          );
          setLegTrips(tripsData.filter(Boolean));
        } else if (data?.trip_id) {
          const tripData = await getTripDetails(data.trip_id);
          setTrip(tripData);
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          navigate("/login", {
            replace: true,
            state: { from: { pathname: `/booking/${id}`, search: "" } },
          });
        } else {
          navigate("/profile");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id, navigate, searchParams]);

  // таймер «очікує оплату»
  useEffect(() => {
    if (!booking?.expires_at || booking.payment_status !== "pending") return;

    const tick = () => {
      const diff = Math.max(0, new Date(booking.expires_at) - Date.now());
      setTimeLeft({
        minutes: Math.floor(diff / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
      if (diff <= 0) setHoldExpired(true);
      return diff <= 0;
    };

    if (tick()) return undefined;
    const interval = setInterval(() => {
      if (tick()) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [booking]);

  const handleDownload = () => {
    downloadTickets(booking.id);
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await cancelBookingById(booking.id);
      setBooking({ ...booking, payment_status: "cancelled" });
      enqueueSnackbar(t("booking.cancelled"), { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.error || t("booking.cancelError"),
        { variant: "error" }
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRefund = async () => {
    setIsRefunding(true);
    try {
      const response = await refundBooking(booking.id);
      setBooking({ ...booking, payment_status: response.status });
      enqueueSnackbar(response.message, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.error || t("booking.refundInitError"),
        { variant: "error" }
      );
    } finally {
      setIsRefunding(false);
      setRefundConfirmationOpen(false);
    }
  };

  const directionAction = (leg) => {
    if (!booking?.is_round_trip || isPartnerBooking) return null;
    const legStatus = leg?.payment_status;
    if (
      !legStatus ||
      ["cancelled", "refunded", "refunding", "failed", "no_show"].includes(legStatus)
    ) {
      return null;
    }
    if (legStatus === "paid" && booking.payment_method === "card") {
      const departure = leg?.from?.departure_datetime;
      if (departure && dayjs(departure).diff(dayjs(), "hour", true) < 2) return null;
      return "refund";
    }
    if (legStatus === "paid" && booking.payment_method === "on_site") return "cancel";
    if (["pending", "not_paid", "processing"].includes(legStatus)) return "cancel";
    return null;
  };

  const handleDirection = async () => {
    const { leg, action } = directionDialog;
    if (!leg || !action) return;
    setIsCancelling(true);
    try {
      if (action === "refund") {
        await refundBooking(booking.id, leg.leg_index);
        enqueueSnackbar(t("booking.directionRefundRequested"), { variant: "success" });
      } else {
        await cancelBookingById(booking.id, leg.leg_index);
        enqueueSnackbar(t("booking.directionCancelled"), { variant: "success" });
      }
      setBooking(await getBookingDetails(booking.id));
      setDirectionDialog({ open: false, leg: null, label: "", action: null });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.error || error?.message || t("booking.cancelError"),
        { variant: "error" }
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const isPartnerBooking =
    booking?.source === "contrabus_sale" ||
    booking?.provider === "partner" ||
    booking?.provider === "contrabus";

  const getRefundPolicyText = () => {
    if (isPartnerBooking) {
      return t("refund.partnerRules", {
        defaultValue: "Повернення за правилами стороннього перевізника (див. оферту).",
      });
    }
    if (!trip) return "";
    return refundTextForHours(
      dayjs(activeTrip?.departure_date).diff(dayjs(), "hour"),
      t
    );
  };

  const getPassengerRefundText = (passenger) => {
    if (isPartnerBooking) {
      return t("refund.partnerRules", {
        defaultValue: "Повернення за правилами стороннього перевізника (див. оферту).",
      });
    }
    if (!trip || !passenger) return "";
    return refundTextForHours(
      dayjs(activeTrip?.departure_date).diff(dayjs(), "hour"),
      t
    );
  };

  const proceedToPayment = async (confirmNewPrice = false) => {
    setIsPaying(true);
    try {
      await initiatePayment(booking.id, { confirmNewPrice });
    } catch (error) {
      setIsPaying(false);
      if (error?.response?.status === 409 && error.response.data?.price_changed) {
        setPriceChangeDialog({
          open: true,
          bookedPrice: error.response.data.booked_price,
          currentPrice: error.response.data.current_price,
        });
        return;
      }
      enqueueSnackbar(
        error?.response?.data?.error || t("booking.paymentCreateError"),
        { variant: "error" }
      );
    }
  };

  const handlePay = async () => {
    try {
      const quote = await checkPaymentPrice(booking.id);
      if (quote.price_changed) {
        setPriceChangeDialog({
          open: true,
          bookedPrice: quote.booked_price,
          currentPrice: quote.current_price,
        });
        return;
      }
      await proceedToPayment(false);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.error || t("booking.paymentCreateError"),
        { variant: "error" }
      );
    }
  };

  const handleConfirmNewPrice = async () => {
    const newPrice = priceChangeDialog.currentPrice;
    setPriceChangeDialog({ open: false, bookedPrice: null, currentPrice: null });
    setBooking((prev) => ({
      ...prev,
      price: newPrice,
      current_price: newPrice,
      price_changed: false,
    }));
    await proceedToPayment(true);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleOpenMap = (address) => {
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(mapUrl, "_blank");
  };

  if (loading) {
    return (
      <PageContainer sx={{ py: 6 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} />
          <Typography color="text.secondary">{t("booking.loadingDetails")}</Typography>
        </Stack>
      </PageContainer>
    );
  }

  const isPartner =
    booking?.source === "contrabus_sale" ||
    booking?.provider === "partner" ||
    booking?.provider === "contrabus";

  if (!booking || (!trip && !booking.is_transfer && !booking.is_round_trip && !isPartner)) {
    return (
      <PageContainer sx={{ py: 6, textAlign: "center" }}>
        <Typography color="text.secondary">{t("booking.notFound")}</Typography>
        <Button onClick={() => navigate("/profile")} sx={{ mt: 2 }}>{t("booking.backToProfile")}</Button>
      </PageContainer>
    );
  }

  const activeTrip = (booking.is_transfer || booking.is_round_trip) ? legTrips[0] : trip;

  const moneyCents = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  };
  const legMoney = (leg) => {
    const people = leg?.passengers || [];
    const rows = people.filter(
      (p) => p.refund_amount != null && Number(p.refund_amount) > 0
    );
    let refunded = 0;
    let awaiting = 0;
    if (rows.length) {
      rows.forEach((p) => {
        const part = moneyCents(p.refund_amount);
        if (p.refund_status === "confirmed" || p.status === "refunded") refunded += part;
        else awaiting += part;
      });
      return { refunded, awaiting };
    }
    const legStatus = leg?.payment_status;
    if (
      (legStatus === "refunding" || legStatus === "refunded") &&
      leg?.refund_amount != null
    ) {
      const part = moneyCents(leg.refund_amount);
      if (legStatus === "refunded") refunded += part;
      else awaiting += part;
    }
    return { refunded, awaiting };
  };
  const moneyLegs = booking.legs?.length ? booking.legs : [booking];
  const refundedCents = moneyLegs.reduce((sum, leg) => sum + legMoney(leg).refunded, 0);
  const awaitingCents = moneyLegs.reduce((sum, leg) => sum + legMoney(leg).awaiting, 0);
  const hasRefundMovement = refundedCents > 0 || awaitingCents > 0;
  const fromCents = (cents) => cents / 100;
  const uah = (amount) => t("units.uahAmount", { amount });
  const refundNote = (leg) => {
    const money = legMoney(leg);
    return (
      <>
        {money.refunded > 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("booking.refundedOf", { amount: uah(fromCents(money.refunded)) })}
          </Typography>
        ) : null}
        {money.awaiting > 0 ? (
          <Typography variant="body2" color="warning.main">
            {t("booking.awaitingOf", { amount: uah(fromCents(money.awaiting)) })}
          </Typography>
        ) : null}
      </>
    );
  };
  const passengerRefundLine = (p) => {
    const amount =
      p.refund_amount != null && Number(p.refund_amount) > 0
        ? ` · ${uah(p.refund_amount)}`
        : "";
    if (p.status === "refunded" || p.refund_status === "confirmed") {
      return `${t("booking.ticketRefunded")}${amount}`;
    }
    if (p.status === "refunding") {
      return `${t("booking.awaitingRefund")}${amount}`;
    }
    return "";
  };
  const passengerCards = () => {
    if (!booking.is_transfer) {
      return (booking.passengers || []).map((passenger) => ({
        person: passenger,
        tickets: [{ passenger, segment: "", legIndex: null }],
      }));
    }
    const legs = [...(booking.legs || [])].sort(
      (a, b) => (a.leg_index || 0) - (b.leg_index || 0)
    );
    const rows = legs.map((leg) =>
      [...(leg.passengers || [])].sort((a, b) => (a.id || 0) - (b.id || 0))
    );
    const count = rows.reduce((max, people) => Math.max(max, people.length), 0);
    const cards = [];
    for (let index = 0; index < count; index += 1) {
      const tickets = [];
      legs.forEach((leg, legPosition) => {
        const passenger = rows[legPosition][index];
        if (!passenger) return;
        tickets.push({
          passenger,
          legIndex: leg.leg_index,
          segment: `${getCityDisplay(leg.from?.city)} → ${getCityDisplay(leg.to?.city)}`,
        });
      });
      if (tickets.length) cards.push({ person: tickets[0].passenger, tickets });
    }
    return cards;
  };

  const renderLegRoute = (leg, legTrip, legLabel, { hideTitle = false, hideActions = false } = {}) => {
    if (!legTrip) return null;
    const legStops = adjustTimes(dayjs(legTrip.departure_date), legTrip.route?.stops ?? []);
    const legFromIndex = legStops.findIndex((s) => getCityKey(s.city) === getCityKey(leg.from?.city));
    const legToIndex = legStops.findIndex((s) => getCityKey(s.city) === getCityKey(leg.to?.city));
    const legFull =
      legFromIndex >= 0 && legToIndex >= 0
        ? legStops.slice(legFromIndex, legToIndex + 1)
        : [];
    const legSliced =
      showAllStops || legFull.length <= 2
        ? legFull
        : [legFull[0], legFull[legFull.length - 1]];

    return (
      <Box key={leg.leg_index} sx={{ mb: 3 }}>
        {!hideTitle && <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {legLabel}: {getCityDisplay(leg.from?.city)} → {getCityDisplay(leg.to?.city)}
          </Typography>
          {leg.payment_status === "refunding" && (
            <Chip size="small" color="warning" label={t("booking.refunding")} />
          )}
          {leg.payment_status === "refunded" && (
            <Chip size="small" color="default" label={t("booking.refunded")} />
          )}
          {leg.payment_status === "cancelled" && (
            <Chip size="small" color="default" label={t("booking.directionStatusCancelled")} />
          )}
        </Box>}
        {refundNote(leg)}
        {(leg.carrier || legTrip.route?.carrier) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
            {t("checkout.carrier")}: {leg.carrier || legTrip.route?.carrier}
          </Typography>
        )}
        {legSliced.map((stop, index) => {
          const isLast = index === legSliced.length - 1;
          const displayTime = isLast
            ? (stop.arrival_datetime ?? stop.departure_datetime)
            : (stop.departure_datetime ?? stop.arrival_datetime);
          const label = isLast ? t("common.arrival") : t("common.departure");
          if (!displayTime) return null;
          return (
            <Box key={`${leg.leg_index}-${index}`}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7rem" }}>
                      {label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main", fontVariantNumeric: "tabular-nums" }}>
                      {displayTime.format("HH:mm")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                      {displayTime.format("DD.MM.YYYY")}
                    </Typography>
                  </Box>
                  <CircleIcon sx={{ fontSize: 10, color: "primary.main", mx: 1.5, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {getCityDisplay(stop.city)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                      {stop.address}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {index !== legSliced.length - 1 && (
                <Box sx={{ borderLeft: `2px dashed ${theme.palette.divider}`, height: 20, ml: "19px", mb: 1 }} />
              )}
            </Box>
          );
        })}
        {!hideActions && directionAction(leg) && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<CancelIcon />}
            onClick={() =>
              setDirectionDialog({
                open: true,
                leg,
                label: `${legLabel}: ${getCityDisplay(leg.from?.city)} → ${getCityDisplay(leg.to?.city)}`,
                action: directionAction(leg),
              })
            }
            sx={{ mt: 1 }}
          >
            {t("booking.cancelDirection")}
          </Button>
        )}
      </Box>
    );
  };

  // Розрахунок часу для кожної зупинки (базова дата + час; при переході через північ додається день)
  const stops = activeTrip ? adjustTimes(dayjs(activeTrip.departure_date), activeTrip.route?.stops ?? []) : [];
  const fromIndex = stops.findIndex((s) => getCityKey(s.city) === getCityKey(booking.from?.city));
  const toIndex = stops.findIndex((s) => getCityKey(s.city) === getCityKey(booking.to?.city));
  const slicedStops =
    fromIndex >= 0 && toIndex >= 0
      ? showAllStops
        ? stops.slice(fromIndex, toIndex + 1)
        : [stops[fromIndex], stops[toIndex]]
      : [];

  // Для останньої зупинки показуємо час прибуття; для інших — час відправлення
  const getStopDisplayTime = (stop, index) => {
    const isLast = index === slicedStops.length - 1;
    const dt = isLast
      ? (stop.arrival_datetime ?? stop.departure_datetime)
      : (stop.departure_datetime ?? stop.arrival_datetime);
    return dt;
  };
  const getStopLabel = (index) =>
    index === slicedStops.length - 1 ? t("common.arrival") : t("common.departure");

  // ---- ЄДИНА ЛОГІКА СТАНІВ ----
  const parseBookingDt = (value) => {
    if (!value) return null;
    const parsed = dayjs(value, "YYYY-MM-DD HH:mm");
    if (parsed.isValid()) return parsed;
    const fallback = dayjs(value);
    return fallback.isValid() ? fallback : null;
  };

  const departureAt = isPartner
    ? (parseBookingDt(booking?.from?.departure_datetime) || dayjs())
    : activeTrip
      ? dayjs(activeTrip.departure_date)
      : dayjs();
  const now = dayjs();

  const lastLegTrip =
    (booking.is_transfer || booking.is_round_trip) && legTrips.length > 0
      ? legTrips[legTrips.length - 1]
      : activeTrip;
  const journeyEndAt =
    parseBookingDt(booking?.to?.arrival_datetime) ||
    parseBookingDt(booking?.to?.departure_datetime) ||
    (lastLegTrip ? dayjs(lastLegTrip.departure_date) : departureAt);

  // Блокуємо дії лише якщо після завершення поїздки минуло ≥ 24 год
  const isTripLocked =
    journeyEndAt.isValid() &&
    journeyEndAt.isBefore(now) &&
    now.diff(journeyEndAt, "hour", true) >= 24;

  const status = booking.payment_status;
  const paymentMethod = booking.payment_method || "card";

  const isCancelledLike = ["cancelled", "refunded", "failed", "expired"].includes(status);
  const isPaid = status === "paid";
  const isPendingLike = ["pending", "not_paid", "processing"].includes(status);

  // актуальне = не скасоване і поїздка ще не «закрита» (24 год після завершення)
  const isActive = !isCancelledLike && !isTripLocked;

  // Скільки годин залишилось — для політики повернення
  const hoursLeft = departureAt.diff(now, "hour");
  const canBeRefunded = hoursLeft >= 2;

  // ✅ квитки можна завантажити:
  //    - якщо оплачено
  //    - АБО not_paid (оплата на місці / pay-later)
  //    - АБО метод on_site
  // Короткий card-hold (pending/processing) — лише після оплати
  const canDownloadTickets =
    isActive && (isPaid || status === "not_paid" || paymentMethod === "on_site");

  // Оплатити можна лише активне бронювання, яке ще чекає оплату.
  // refunding / cancelled / refunded кнопку не показують.
  const canPayNow = isActive && isPendingLike && !holdExpired;
  const canCancelUnpaid = isActive && !isPaid && isPendingLike && !holdExpired;

  // можна скасувати з поверненням, якщо оплачено, актуально і ще є час за політикою
  const canCancelPaidWithRefund = isActive && isPaid && canBeRefunded;

  const getStatusChip = (s) => {
    switch (s) {
      case "paid":
        return <Chip icon={<CheckCircleIcon />} label={t("booking.paid")} color="success" variant="outlined" />;
      case "not_paid":
        return <Chip icon={<WarningAmberIcon />} label={t("booking.notPaid")} color="warning" variant="outlined" />;
      case "pending":
        return <Chip icon={<WarningAmberIcon />} label={t("booking.pendingPayment")} color="warning" variant="outlined" />;
      case "processing":
        return <Chip icon={<InfoIcon />} label={t("booking.processingPayment")} color="info" variant="outlined" />;
      case "cancelled":
        return <Chip icon={<CancelIcon />} label={t("status.cancelled")} color="error" variant="outlined" />;
      case "expired":
        return <Chip icon={<WarningAmberIcon />} label={t("status.expired")} color="warning" variant="outlined" />;
      case "refunded":
        return <Chip icon={<InfoIcon />} label={t("booking.refunded")} color="info" variant="outlined" />;
      case "refunding":
        return <Chip icon={<InfoIcon />} label={t("booking.refunding")} color="warning" variant="outlined" />;
      case "failed":
        return <Chip icon={<ErrorIcon />} label={t("booking.paymentFailed")} color="error" variant="outlined" />;
      default:
        return <Chip label={s} variant="outlined" />;
    }
  };

  const transferIntermediateCount = booking.is_transfer
    ? (booking.legs || []).reduce((sum, leg, idx) => {
        const legTrip = legTrips[idx];
        if (!legTrip) return sum;
        const legStops = adjustTimes(dayjs(legTrip.departure_date), legTrip.route?.stops ?? []);
        const fromIdx = legStops.findIndex((s) => getCityKey(s.city) === getCityKey(leg.from?.city));
        const toIdx = legStops.findIndex((s) => getCityKey(s.city) === getCityKey(leg.to?.city));
        if (fromIdx < 0 || toIdx < 0) return sum;
        return sum + Math.max(0, toIdx - fromIdx - 1);
      }, 0)
    : 0;
  const intermediateCount = booking.is_transfer
    ? transferIntermediateCount
    : Math.max(0, toIndex - fromIndex - 1);

  const directionLabel = (leg) =>
    leg.leg_index === 1 ? t("roundTrip.outbound") : t("roundTrip.return");

  const canDownloadLeg = (leg) => {
    const legStatus = leg?.payment_status;
    if (["cancelled", "refunded", "failed", "refunding"].includes(legStatus)) return false;
    return legStatus === "paid" || legStatus === "not_paid" || paymentMethod === "on_site";
  };

  const renderDirectionCard = (leg, idx) => {
    const label = directionLabel(leg);
    const routeLabel = `${label}: ${getCityDisplay(leg.from?.city)} → ${getCityDisplay(leg.to?.city)}`;
    const passengers = leg.passengers || [];
    return (
      <Paper
        key={leg.leg_index}
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, flexWrap: "wrap", mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {routeLabel}
          </Typography>
          {getStatusChip(leg.payment_status)}
        </Box>
        {leg.price != null && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {t("booking.directionPrice", { amount: leg.price })}
          </Typography>
        )}
        <Box sx={{ mb: 2 }}>{refundNote(leg)}</Box>
        {renderLegRoute(leg, legTrips[idx], label, { hideTitle: true, hideActions: true })}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          {t("booking.passengersTitle")}
        </Typography>
        {passengers.map((p) => {
          const seatOpen = p.status === "active" && !p.is_used;
          return (
            <Box
              key={p.id}
              sx={{ mb: 1.5, backgroundColor: "action.hover", p: 2, borderRadius: 2 }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Box>
                  <Typography>
                    {p.name} {p.surname}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("checkout.passengerType")}: {p.type === "adult" ? t("booking.typeAdult") : t("booking.typeChild")}
                  </Typography>
                  {passengerRefundLine(p) && (
                    <Typography
                      variant="body2"
                      color={p.status === "refunded" ? "text.secondary" : "warning.main"}
                      sx={{ mt: 0.5 }}
                    >
                      {passengerRefundLine(p)}
                    </Typography>
                  )}
                  {p.status === "cancelled" && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t("booking.directionStatusCancelled")}
                    </Typography>
                  )}
                </Box>
                {seatOpen && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {canDownloadLeg(leg) && (
                      <Tooltip title={t("booking.downloadTicket")}>
                        <IconButton
                          color="primary"
                          onClick={() => downloadPassengerTicket(booking.id, p.id, leg.leg_index)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {directionAction(leg) && (
                      <Tooltip title={t("booking.cancelPassengerSeat")}>
                        <IconButton
                          color="warning"
                          onClick={() =>
                            setConfirmPassengerDialog({
                              open: true,
                              passenger: p,
                              direction: routeLabel,
                            })
                          }
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!canDownloadLeg(leg)}
            onClick={() => downloadTickets(booking.id, leg.leg_index)}
          >
            {t("booking.directionTickets")}
          </Button>
          {directionAction(leg) && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<CancelIcon />}
              onClick={() =>
                setDirectionDialog({
                  open: true,
                  leg,
                  label: routeLabel,
                  action: directionAction(leg),
                })
              }
            >
              {t("booking.cancelDirection")}
            </Button>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <PageContainer sx={{ py: { xs: 2, md: 4 }, mb: 6 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, textAlign: "center" }}>
        {t("booking.details", { id: booking.id })}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>{getStatusChip(status)}</Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {booking.is_round_trip && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("booking.separateDirectionsHint")}
              </Typography>
              {booking.legs?.map((leg, idx) => renderDirectionCard(leg, idx))}
            </>
          )}
          {!booking.is_round_trip && <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              mb: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {booking.is_transfer
                ? t("bookingDetails.multiLeg")
                : isPartner
                  ? t("bookingDetails.partnerRoute", { defaultValue: "Маршрут (партнер)" })
                  : t("booking.route")}
            </Typography>
            {isPartner && (
              <Box>
                {booking.carrier && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("checkout.carrier")}: {booking.carrier}
                  </Typography>
                )}
                {[booking.from, booking.to].filter(Boolean).map((stop, index, arr) => {
                  const isLast = index === arr.length - 1;
                  const rawTime = isLast
                    ? stop.arrival_datetime || stop.departure_datetime
                    : stop.departure_datetime || stop.arrival_datetime;
                  const displayTime = parseBookingDt(rawTime);
                  return (
                    <Box key={`partner-stop-${index}`}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
                          <Box sx={{ flexShrink: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", fontSize: "0.7rem" }}
                            >
                              {isLast ? t("common.arrival") : t("common.departure")}
                            </Typography>
                            {displayTime && (
                              <>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: "primary.main",
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {displayTime.format("HH:mm")}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {displayTime.format("DD.MM.YYYY")}
                                </Typography>
                              </>
                            )}
                            {!displayTime && booking.date && (
                              <Typography variant="body2" color="text.secondary">
                                {booking.date}
                              </Typography>
                            )}
                          </Box>
                          <CircleIcon
                            sx={{ fontSize: 10, color: "primary.main", mx: 1.5, flexShrink: 0 }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {getCityDisplay(stop.city)}
                            </Typography>
                            {stop.address && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.8125rem" }}
                              >
                                {stop.address}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      {!isLast && (
                        <Box
                          sx={{
                            borderLeft: `2px dashed ${theme.palette.divider}`,
                            height: 20,
                            ml: "19px",
                            mb: 1,
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            {booking.is_transfer && booking.legs?.map((leg, idx) =>
              renderLegRoute(
                leg,
                legTrips[idx],
                leg.leg_index === 1 ? t("checkout.leg1") : t("checkout.leg2")
              )
            )}
            {!booking.is_transfer && !booking.is_round_trip && !isPartner && slicedStops.map((stop, index) => {
              const displayTime = getStopDisplayTime(stop, index);
              const label = getStopLabel(index);
              if (!displayTime) return null;
              return (
                <Box key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
                      <Box sx={{ flexShrink: 0 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", fontSize: "0.7rem" }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "primary.main",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {displayTime.format("HH:mm")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                          {displayTime.format("DD.MM.YYYY")}
                        </Typography>
                      </Box>
                      <CircleIcon sx={{ fontSize: 10, color: "primary.main", mx: 1.5, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {getCityDisplay(stop.city)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: { xs: 180, sm: 280, md: 400 },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "0.8125rem",
                          }}
                        >
                          {stop.address}
                        </Typography>
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t("booking.copyAddress")}>
                        <IconButton size="small" onClick={() => handleCopy(`${getCityDisplay(stop.city)} ${stop.address}`)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("booking.viewOnMap")}>
                        <IconButton size="small" onClick={() => handleOpenMap(`${getCityDisplay(stop.city)} ${stop.address}`)}>
                          <MapIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                  {index !== slicedStops.length - 1 && (
                    <Box
                      sx={{
                        borderLeft: `2px dashed ${theme.palette.divider}`,
                        height: 20,
                        ml: "19px",
                        mb: 1,
                      }}
                    />
                  )}
                </Box>
              );
            })}
            {intermediateCount > 0 && (
              <Button
                onClick={() => setShowAllStops(!showAllStops)}
                startIcon={<ScheduleIcon />}
                sx={{ textTransform: "none" }}
              >
                {showAllStops
                  ? t("checkout.hideStops")
                  : t("checkout.showStops", { count: intermediateCount })}
              </Button>
            )}
          </Paper>}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              mb: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t("booking.contactInfo")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Email:</strong> {booking.buyer_email}
            </Typography>
            <Typography variant="body2">
              <strong>{t("admin.phone")}:</strong> {booking.buyer_phone}
            </Typography>
          </Paper>

          {!booking.is_round_trip && <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t("booking.passengersTitle")}
            </Typography>
            {passengerCards().map((card) => {
              const ticketActions = (ticket) =>
                canDownloadTickets && ticket.passenger.status === "active" ? (
                  <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                    <Tooltip title={t("booking.downloadTicket")}>
                      <IconButton
                        color="primary"
                        onClick={() =>
                          downloadPassengerTicket(
                            booking.id,
                            ticket.passenger.id,
                            ticket.legIndex
                          )
                        }
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    {canCancelPaidWithRefund ? (
                      <Tooltip title={t("booking.cancelPassengerTicket", { defaultValue: "Скасувати квиток" })}>
                        <IconButton
                          color="warning"
                          onClick={() =>
                            setConfirmPassengerDialog({
                              open: true,
                              passenger: ticket.passenger,
                              direction: booking.is_transfer ? "" : ticket.segment,
                            })
                          }
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                ) : null;
              const refundCaption = (ticket) => {
                const note = passengerRefundLine(ticket.passenger);
                if (!note) return null;
                return (
                  <Typography
                    variant="body2"
                    color={ticket.passenger.status === "refunded" ? "text.secondary" : "error"}
                  >
                    {note}
                  </Typography>
                );
              };
              return (
                <Box
                  key={card.person.id}
                  sx={{
                    mb: 2,
                    backgroundColor: "action.hover",
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <Box>
                      <Typography>
                        {card.person.name} {card.person.surname}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("checkout.passengerType")}: {card.person.type === "adult" ? t("booking.typeAdult") : t("booking.typeChild")}
                      </Typography>
                      {!booking.is_transfer ? refundCaption(card.tickets[0]) : null}
                    </Box>
                    {!booking.is_transfer ? ticketActions(card.tickets[0]) : null}
                  </Box>
                  {booking.is_transfer
                    ? card.tickets.map((ticket) => (
                        <Box
                          key={ticket.passenger.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {ticket.segment}
                            </Typography>
                            {refundCaption(ticket)}
                          </Box>
                          {ticketActions(ticket)}
                        </Box>
                      ))
                    : null}
                </Box>
              );
            })}
          </Paper>}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ "&:last-child": { pb: 0 } }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t("booking.paymentTitle")}
              </Typography>
              {refundedCents > 0 ? (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t("booking.refunded")}: <strong>{uah(fromCents(refundedCents))}</strong>
                </Typography>
              ) : null}
              {awaitingCents > 0 ? (
                <Typography variant="body1" color="warning.main" sx={{ mb: 1 }}>
                  {t("booking.awaitingRefund")}: <strong>{uah(fromCents(awaitingCents))}</strong>
                </Typography>
              ) : null}
              <Typography
                variant={hasRefundMovement ? "body2" : "body1"}
                color={hasRefundMovement ? "text.secondary" : "text.primary"}
                sx={{ mb: 2 }}
              >
                {isPendingLike && !hasRefundMovement
                  ? t("booking.amountToPay")
                  : t("booking.paid")}
                : <strong>{uah(booking.price)}</strong>
              </Typography>

              {booking.show_price_notice && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("booking.priceMayChangeNotice")}
                </Alert>
              )}

              {booking.price_changed && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  {t("booking.newPrice")}: <strong>{t("units.uahAmount", { amount: booking.current_price })}</strong>
                </Typography>
              )}

              {status === "pending" && timeLeft && !holdExpired && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2, fontVariantNumeric: "tabular-nums" }}>
                  {t("booking.payBy")} {String(timeLeft.minutes).padStart(2, "0")}:
                  {String(timeLeft.seconds).padStart(2, "0")}
                </Typography>
              )}

              {(holdExpired || status === "expired") && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t("booking.holdExpired")}
                </Alert>
              )}

              {/* Завантажити квиток — оплачено АБО on_site, і бронювання актуальне */}
              {!booking.is_round_trip && (
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  fullWidth
                  disabled={!canDownloadTickets}
                  sx={{ mb: 2 }}
                >
                  {t("booking.downloadTicket")}
                </Button>
              )}

              {/* Оплатити зараз — якщо не оплачено, актуальне, і метод — картка */}
              {canPayNow && (
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={isPaying ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : <PaymentIcon />}
                  onClick={handlePay}
                  disabled={isPaying}
                  sx={{ mb: 2, fontWeight: "bold", py: 1.5 }}
                >
                  {isPaying ? t("booking.processing") : t("booking.payBooking")}
                </Button>
              )}

              {/* Скасувати бронювання (не оплачено) */}
              {canCancelUnpaid && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={
                    isCancelling ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <CancelIcon />
                    )
                  }
                  onClick={handleCancel}
                  disabled={isCancelling || isPaying}
                >
                  {isCancelling ? t("booking.processing") : t("booking.cancelBooking")}
                </Button>
              )}

              {/* Скасувати з поверненням (оплачено, ще є час) */}
              {canCancelPaidWithRefund && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  startIcon={<CancelIcon />}
                  onClick={() => setRefundConfirmationOpen(true)}
                  disabled={isCancelling || isRefunding || isPaying}
                  sx={{ mt: 1 }}
                >
                  {t("booking.cancelBooking")}
                </Button>
              )}

              <Button
                fullWidth
                variant="text"
                onClick={() => navigate("/profile")}
                sx={{ mt: 2, textTransform: "none" }}
              >
                {t("booking.backToProfile")}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Діалог зміни ціни перед оплатою */}
      <Dialog
        open={priceChangeDialog.open}
        onClose={() => setPriceChangeDialog({ open: false, bookedPrice: null, currentPrice: null })}
      >
        <DialogTitle>{t("booking.priceChangedTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("booking.priceChangedText")}
          </DialogContentText>
          <Typography variant="body2">
            {t("booking.bookedPrice")}: <strong>{t("units.uahAmount", { amount: priceChangeDialog.bookedPrice })}</strong>
          </Typography>
          <Typography variant="body2" color="warning.main">
            {t("booking.newPrice")}: <strong>{t("units.uahAmount", { amount: priceChangeDialog.currentPrice })}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceChangeDialog({ open: false, bookedPrice: null, currentPrice: null })}>
            {t("booking.cancel")}
          </Button>
          <Button onClick={handleConfirmNewPrice} color="success" variant="contained" disabled={isPaying}>
            {isPaying ? t("booking.processing") : t("booking.payNewPrice")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={directionDialog.open}
        onClose={() =>
          setDirectionDialog({ open: false, leg: null, label: "", action: null })
        }
      >
        <DialogTitle>{t("booking.cancelDirection")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {directionDialog.action === "refund"
              ? t("booking.confirmCancelDirectionRefund", {
                  direction: directionDialog.label,
                  id: booking.id,
                })
              : t("booking.confirmCancelDirection", {
                  direction: directionDialog.label,
                  id: booking.id,
                })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDirectionDialog({ open: false, leg: null, label: "", action: null })
            }
            disabled={isCancelling}
          >
            {t("booking.cancel")}
          </Button>
          <Button onClick={handleDirection} color="warning" disabled={isCancelling}>
            {isCancelling && (
              <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />
            )}
            {isCancelling ? t("booking.processing") : t("booking.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог підтвердження повернення всього бронювання */}
      <Dialog open={refundConfirmationOpen} onClose={() => setRefundConfirmationOpen(false)}>
        <DialogTitle>{t("booking.confirmRefundTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("booking.confirmRefundText")}
            <br />
            {getRefundPolicyText()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundConfirmationOpen(false)}>{t("booking.cancel")}</Button>
          <Button onClick={handleRefund} color="warning" disabled={isRefunding}>
            {isRefunding && (
              <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />
            )}
            {isRefunding ? t("booking.processing") : t("booking.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог підтвердження скасування квитка по пасажиру */}
      <Dialog
        open={confirmPassengerDialog.open}
        onClose={() => setConfirmPassengerDialog({ open: false, passenger: null, direction: "" })}
      >
        <DialogTitle>{t("booking.cancelTicketConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {booking?.is_transfer
              ? t("booking.confirmCancelTransferPassenger", {
                  name: `${confirmPassengerDialog.passenger?.name || ""} ${confirmPassengerDialog.passenger?.surname || ""}`.trim(),
                })
              : confirmPassengerDialog.direction
              ? t("booking.confirmCancelPassengerSeat", {
                  name: `${confirmPassengerDialog.passenger?.name || ""} ${confirmPassengerDialog.passenger?.surname || ""}`.trim(),
                  direction: confirmPassengerDialog.direction,
                })
              : (
                <>
                  {t("booking.cancelTicketConfirmText")}{" "}
                  <strong>
                    {confirmPassengerDialog.passenger?.name}{" "}
                    {confirmPassengerDialog.passenger?.surname}
                  </strong>
                  ?
                </>
              )}
          </DialogContentText>

          <Typography variant="body2" color="text.secondary">
            {getPassengerRefundText(confirmPassengerDialog.passenger)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmPassengerDialog({ open: false, passenger: null, direction: "" })}
            disabled={isCancelling}
          >
            {t("booking.cancel")}
          </Button>
          <Button
            color="warning"
            disabled={isCancelling}
            onClick={async () => {
              const passenger = confirmPassengerDialog.passenger;
              setIsCancelling(true);
              try {
                const response = await cancelPassengerTicket(booking.id, passenger.id);
                enqueueSnackbar(response.message || t("booking.passengerTicketCancelled"), { variant: "success" });
                setBooking(await getBookingDetails(booking.id));
                setConfirmPassengerDialog({ open: false, passenger: null, direction: "" });
              } catch (e) {
                enqueueSnackbar(e?.message || t("booking.cancelTicketError"), { variant: "error" });
              } finally {
                setIsCancelling(false);
              }
            }}
          >
            {isCancelling && (
              <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />
            )}
            {isCancelling ? t("booking.processing") : t("booking.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default BookingDetails;
